import React, { useState } from 'react';
import { HelpCircle, X, Lightbulb, Calculator, DollarSign, TrendingUp } from 'lucide-react';

/**
 * Contextual Help and Tips System
 * Provides inline guidance for each calculator section
 */
export default function CalculatorHelp({ section, collapsed = false }) {
  const [isOpen, setIsOpen] = useState(!collapsed);

  const helpContent = {
    clientInfo: {
      title: 'Client & Service Information',
      icon: Calculator,
      tips: [
        'Select the service type (e.g., Child Support, Traffic) to load appropriate fee structures',
        'Municipality and State help identify location-specific requirements',
        'Card and ACH processors determine interchange fees and costs',
        'Reader quantity affects equipment costs in the final calculation'
      ],
      examples: [
        { label: 'Municipality', value: 'San Francisco' },
        { label: 'Service', value: 'Child Support' },
        { label: 'Readers', value: '0-5 typically' }
      ]
    },
    volume: {
      title: 'Volume Assumptions',
      icon: TrendingUp,
      tips: [
        'Total Annual Dollar Volume: Enter in thousands (1000 = $1 million)',
        'Total Transactions: Annual transaction count across all payment types',
        'Payment Mix: Must sum to 100%. Shows the % split between ACH/CC/Amex/Debit',
        'Auto-calculation: Total Volume is calculated from transactions if left empty'
      ],
      examples: [
        { label: 'Annual Volume', value: '1000 = $1,000,000' },
        { label: 'Transactions', value: '50,000/year' },
        { label: 'Payment Mix', value: 'ACH 14.5%, CC 62%, Amex 8.5%, Debit 15%' }
      ],
      formula: 'Total Fees = (Volume × Take Rate) + (Transactions × Min Fees)'
    },
    incumbentPricing: {
      title: 'Incumbent (Current) Pricing',
      icon: DollarSign,
      tips: [
        'Enter the CURRENT pricing your client is paying',
        'Tiered Pricing: Define transaction amount ranges (tiers) and their minimum fees',
        'Take Rates: Percentage charged on each payment (2.5% = enter 2.5)',
        'Min Fees: Minimum charge per transaction regardless of amount',
        'Typical Take Rates: ACH 2-3%, CC 2-3%, Amex 3-4%, Debit 1.5-2.5%'
      ],
      examples: [
        { label: 'Tier 1', value: '$0-$50: Min Fee $1.00' },
        { label: 'Tier 2', value: '$51-$100: Min Fee $1.50' },
        { label: 'ACH Rate', value: '2.50% + $1.00 min' }
      ]
    },
    proposedPricing: {
      title: 'Proposed (New) Pricing',
      icon: DollarSign,
      tips: [
        'Enter YOUR PROPOSED pricing - what you want to charge',
        'Pricing structure mirrors Incumbent section for easy comparison',
        'Lower rates = more competitive, but ensure profitability (check GPM)',
        'System validates against merchant fees to ensure positive margins',
        'Target GPM: Aim for 50-60% for CC, 70-80% for ACH'
      ],
      examples: [
        { label: 'Competitive CC', value: '2.25% + $0.95 min' },
        { label: 'Competitive ACH', value: '2.25% + $0.95 min' },
        { label: 'Premium', value: '2.75% + $1.25 min' }
      ]
    },
    distribution: {
      title: 'Transaction Distribution',
      icon: TrendingUp,
      tips: [
        'Define what % of transactions fall into each dollar amount tier',
        'MUST sum to exactly 100% across all 9 tiers',
        'Use historical data if available, or industry averages',
        'Common distribution: Most transactions in lower tiers (under $50)',
        'System auto-validates that total = 100%'
      ],
      examples: [
        { label: 'Low-value focus', value: '40% under $25, 30% $25-$50' },
        { label: 'Balanced', value: '20-15% in each mid-tier' },
        { label: 'High-value', value: '30% over $250' }
      ],
      formula: 'All percentages must sum to 100%'
    }
  };

  const content = helpContent[section];
  if (!content) return null;

  const Icon = content.icon;

  return (
    <div className={`bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-lg overflow-hidden transition-all ${
      isOpen ? 'mb-4' : 'mb-2'
    }`}>
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-indigo-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-indigo-900">{content.title}</div>
            <div className="text-xs text-indigo-600">Click for helpful tips</div>
          </div>
        </div>
        <HelpCircle className={`w-5 h-5 text-indigo-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Content */}
      {isOpen && (
        <div className="px-4 pb-4 space-y-3">
          {/* Tips */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-indigo-900">Quick Tips</span>
            </div>
            <ul className="space-y-1">
              {content.tips.map((tip, idx) => (
                <li key={idx} className="text-xs text-indigo-800 pl-4 relative before:content-['•'] before:absolute before:left-0">
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Examples */}
          {content.examples && (
            <div className="bg-white rounded-lg p-3 border border-indigo-200">
              <div className="text-xs font-semibold text-indigo-900 mb-2">Examples:</div>
              <div className="space-y-1">
                {content.examples.map((example, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <span className="text-indigo-600 font-medium min-w-[100px]">{example.label}:</span>
                    <span className="text-slate-700">{example.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formula */}
          {content.formula && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
              <div className="text-xs font-mono text-amber-900">
                <strong>Formula:</strong> {content.formula}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
